import {
  createContext,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

const noop = () => undefined;

const SaveStateContext = createContext<boolean | null>(null);

type PublishApi = {
  setSave: (isSaving: boolean | null) => void;
  handlerRef: RefObject<() => void>;
};

const PublishContext = createContext<PublishApi>({ setSave: noop, handlerRef: { current: noop } });

export const RoleEditorSaveProvider = ({ children }: { children: ReactNode }) => {
  const [isSaving, setSave] = useState<boolean | null>(null);
  const handlerRef = useRef<() => void>(noop);
  const api = useMemo(() => ({ setSave, handlerRef }), []);

  return (
    <PublishContext.Provider value={api}>
      <SaveStateContext.Provider value={isSaving}>{children}</SaveStateContext.Provider>
    </PublishContext.Provider>
  );
};

export const RoleEditorSaveButton = () => {
  const { t } = useTranslation();
  const isSaving = useContext(SaveStateContext);
  const { handlerRef } = useContext(PublishContext);

  if (isSaving === null) {
    return null;
  }

  return (
    <Button size="sm" isLoading={isSaving} onClick={() => handlerRef.current()}>
      {t('common.save')}
    </Button>
  );
};

export const usePublishRoleEditorSave = (isSaving: boolean | null, handler: () => void) => {
  const { setSave, handlerRef } = useContext(PublishContext);

  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    setSave(isSaving);
  }, [setSave, isSaving]);

  useEffect(() => () => setSave(null), [setSave]);
};
